import { Label } from "@radix-ui/react-label";
import { useId, type ComponentProps } from "react";
import {
    useFormContext,
    type FieldValues,
    type Path,
    useController,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

type SwitchBaseProps = ComponentProps<typeof Switch>;

interface ArSwitchFieldProps<T extends FieldValues>
    extends Omit<SwitchBaseProps, "name" | "checked" | "onCheckedChange"> {
    name: Path<T>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
    description?: string;
}

export function ArSwitchFormField<T extends FieldValues>({
    label,
    name,
    labelAlign = "items-center",
    description,
    ...rest
}: ArSwitchFieldProps<T>) {
    const {
        control,
        formState: { errors },
    } = useFormContext<T>();

    const {
        field: { value, onChange, ...fieldProps },
    } = useController({
        name,
        control,
    });

    const error = errors[name as keyof typeof errors];
    const id = useId();

    return (
        <div className="flex flex-col items-start gap-2">
            <div className={cn("flex flex-col gap-3", labelAlign)}>
                <div className="flex flex-col gap-1 text-center">
                    <Label
                        htmlFor={id}
                        className={cn(
                            "cursor-pointer text-sm font-medium",
                            error && "text-destructive",
                        )}
                    >
                        {label}
                    </Label>
                    {description && (
                        <p className="text-xs text-muted-foreground">
                            {description}
                        </p>
                    )}
                </div>

                <Switch
                    id={id}
                    checked={value}
                    onCheckedChange={onChange}
                    {...fieldProps}
                    {...rest}
                />
            </div>
            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
